import {useAppDispatch, useAppSelector} from '@lib/store';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {LocalDateString} from '@lib/store/src/data/timeslots/apiTypes';
import {localDateStringToStartOfDay} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {useEffect} from 'react';

/**
 * Combines loading state and data for connection workdays and workspace reservations for a given date.
 * Ensures both are loaded if missing, and returns a single status for use in UI.
 * Intended to replace the getAllBuildingLocations selector.
 */
// Note: this kind of should replace this selector:
// getAllBuildingLocations
export const useConnectionsSchedule = (date: LocalDateString) => {
  const dispatch = useAppDispatch();

  const workdayEntry = useAppSelector((state) => {
    return state.data.timeslots.connectionWorkdaysByDate[date];
  });

  const workspaceReservationEntry = useAppSelector((state) => {
    return state.data.timeslots.connectionWorkspaceReservationsByDate[date];
  });

  useEffect(
    function ensureReservationsLoaded() {
      if (!workspaceReservationEntry) {
        dispatch(actions.fetchWorkspaceReservationsForConnections({date}));
      }
    },
    [date, dispatch, workspaceReservationEntry],
  );

  useEffect(
    function ensureWorkdaysLoaded() {
      if (!workdayEntry) {
        dispatch(
          actions.fetchWorkdaysForConnections({
            from: localDateStringToStartOfDay(date),
            to: localDateStringToStartOfDay(date),
          }),
        );
      }
    },
    [date, dispatch, workdayEntry],
  );

  const error = workdayEntry?.status === 'rejected' || workspaceReservationEntry?.status === 'rejected';
  const pending =
    !workdayEntry ||
    !workspaceReservationEntry ||
    workdayEntry.status === 'pending' ||
    workspaceReservationEntry.status === 'pending';

  // Group connections by their location
  return {
    status: pending ? 'pending' : error ? 'error' : 'fulfilled',
    workdays: workdayEntry?.data ?? [],
    workspaceResevations: workspaceReservationEntry?.data ?? [],
  } as const;
};

export type ReferenceLocation = {buildingId: string; floorId?: string; areaId?: string};
